import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './TradeSalesManagement.css';

const TradeSalesManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [skuNumbers, setSkuNumbers] = useState([]);
  const [existingSales, setExistingSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add new sale form state
  const [newSale, setNewSale] = useState({
    date: '',
    customer: '',
    IGD_SKU_number: '',
    quantity: '',
    totalAmount: ''
  });

  // Modify existing sale form state
  const [editingSale, setEditingSale] = useState(null);
  const [modifiedSale, setModifiedSale] = useState({
    date: '',
    customer: '',
    IGD_SKU_number: '',
    quantity: '',
    totalAmount: ''
  });

  useEffect(() => {
    fetchCustomers();
    fetchSkuNumbers();
    fetchExistingSales();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await apiCall('/api/trade-sales/customers');
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchSkuNumbers = async () => {
    try {
      const response = await apiCall('/api/trade-sales/sku-numbers');
      if (response.ok) {
        const data = await response.json();
        setSkuNumbers(data);
      }
    } catch (error) {
      console.error('Error fetching SKU numbers:', error);
    }
  };

  const fetchExistingSales = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/trade-sales');
      if (response.ok) {
        const data = await response.json();
        setExistingSales(data);
      }
    } catch (error) {
      console.error('Error fetching existing sales:', error);
      setError('Failed to fetch existing sales');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewSale = async (e) => {
    e.preventDefault();
    
    if (!newSale.date || !newSale.customer || !newSale.IGD_SKU_number || !newSale.quantity || !newSale.totalAmount) {
      setError('All fields are required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const pricePerUnit = parseFloat(newSale.totalAmount) / parseFloat(newSale.quantity);
      
      const response = await apiCall('/api/trade-sales', {
        method: 'POST',
        body: JSON.stringify({
          date: newSale.date,
          customer: newSale.customer,
          IGD_SKU_number: newSale.IGD_SKU_number,
          quantity: parseFloat(newSale.quantity),
          price_per_unit: pricePerUnit
        })
      });

      if (response.ok) {
        setSuccess('Sale added successfully!');
        setNewSale({
          date: '',
          customer: '',
          IGD_SKU_number: '',
          quantity: '',
          totalAmount: ''
        });
        fetchExistingSales();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add sale');
      }
    } catch (error) {
      console.error('Error adding sale:', error);
      setError('Failed to add sale');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSale = async (e) => {
    e.preventDefault();
    
    if (!editingSale) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const pricePerUnit = parseFloat(modifiedSale.totalAmount) / parseFloat(modifiedSale.quantity);
      
      const response = await apiCall(`/api/trade-sales/${editingSale.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          date: modifiedSale.date,
          customer: modifiedSale.customer,
          IGD_SKU_number: modifiedSale.IGD_SKU_number,
          quantity: parseFloat(modifiedSale.quantity),
          price_per_unit: pricePerUnit
        })
      });

      if (response.ok) {
        setSuccess('Sale updated successfully!');
        setEditingSale(null);
        setModifiedSale({
          date: '',
          customer: '',
          IGD_SKU_number: '',
          quantity: '',
          totalAmount: ''
        });
        fetchExistingSales();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update sale');
      }
    } catch (error) {
      console.error('Error updating sale:', error);
      setError('Failed to update sale');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this sale?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await apiCall(`/api/trade-sales/${saleId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Sale deleted successfully!');
        fetchExistingSales();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete sale');
      }
    } catch (error) {
      console.error('Error deleting sale:', error);
      setError('Failed to delete sale');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (sale) => {
    setEditingSale(sale);
    setModifiedSale({
      date: sale.date,
      customer: sale.customer,
      IGD_SKU_number: sale.IGD_SKU_number,
      quantity: sale.quantity.toString(),
      totalAmount: (parseFloat(sale.quantity) * parseFloat(sale.price_per_unit)).toString()
    });
  };

  const cancelEditing = () => {
    setEditingSale(null);
    setModifiedSale({
      date: '',
      customer: '',
      IGD_SKU_number: '',
      quantity: '',
      totalAmount: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="trade-sales-management">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Trade Sales Management</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Add new retail sales to the trade_sales_history table</li>
              <li>Modify existing sales records</li>
              <li>Total amount is automatically converted to price per unit</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        {/* Add New Retail Sales Section */}
        <div className="section">
          <h2>Add New Retail Sales</h2>
          <form onSubmit={handleAddNewSale} className="form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="new-date">Date:</label>
                <input
                  type="date"
                  id="new-date"
                  value={newSale.date}
                  onChange={(e) => setNewSale({...newSale, date: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="new-customer">Customer:</label>
                <input
                  type="text"
                  id="new-customer"
                  list="customers-list"
                  value={newSale.customer}
                  onChange={(e) => setNewSale({...newSale, customer: e.target.value})}
                  placeholder="Select or type new customer"
                  required
                />
                <datalist id="customers-list">
                  {customers.map((customer, index) => (
                    <option key={index} value={customer} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="new-sku">IDG SKU Number:</label>
                <select
                  id="new-sku"
                  value={newSale.IGD_SKU_number}
                  onChange={(e) => setNewSale({...newSale, IGD_SKU_number: e.target.value})}
                  required
                >
                  <option value="">Select SKU</option>
                  {skuNumbers.map((sku, index) => (
                    <option key={index} value={sku.IGD_SKU_number}>
                      {sku.IGD_SKU_number} - {sku.IGD_Name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="new-quantity">Quantity:</label>
                <input
                  type="number"
                  id="new-quantity"
                  step="0.01"
                  min="0"
                  value={newSale.quantity}
                  onChange={(e) => setNewSale({...newSale, quantity: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="new-total">Total Amount ($):</label>
                <input
                  type="number"
                  id="new-total"
                  step="0.01"
                  min="0"
                  value={newSale.totalAmount}
                  onChange={(e) => setNewSale({...newSale, totalAmount: e.target.value})}
                  required
                />
              </div>
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Adding...' : 'Add Sale'}
            </button>
          </form>
        </div>

        {/* Modify Existing Sales Section */}
        <div className="section">
          <h2>Modify Existing Sales</h2>
          
          {loading ? (
            <div className="loading">Loading sales data...</div>
          ) : (
            <div className="sales-table-container">
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>SKU</th>
                    <th>Quantity</th>
                    <th>Price/Unit</th>
                    <th>Total Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {existingSales.map((sale) => (
                    <tr key={sale.id}>
                      {editingSale && editingSale.id === sale.id ? (
                        // Editing row
                        <>
                          <td>
                            <input
                              type="date"
                              value={modifiedSale.date}
                              onChange={(e) => setModifiedSale({...modifiedSale, date: e.target.value})}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={modifiedSale.customer}
                              onChange={(e) => setModifiedSale({...modifiedSale, customer: e.target.value})}
                            />
                          </td>
                          <td>
                            <select
                              value={modifiedSale.IGD_SKU_number}
                              onChange={(e) => setModifiedSale({...modifiedSale, IGD_SKU_number: e.target.value})}
                            >
                              {skuNumbers.map((sku, index) => (
                                <option key={index} value={sku.IGD_SKU_number}>
                                  {sku.IGD_SKU_number} - {sku.IGD_Name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={modifiedSale.quantity}
                              onChange={(e) => setModifiedSale({...modifiedSale, quantity: e.target.value})}
                            />
                          </td>
                          <td>
                            {formatCurrency(parseFloat(modifiedSale.totalAmount || 0) / parseFloat(modifiedSale.quantity || 1))}
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={modifiedSale.totalAmount}
                              onChange={(e) => setModifiedSale({...modifiedSale, totalAmount: e.target.value})}
                            />
                          </td>
                          <td>
                            <button onClick={handleUpdateSale} className="save-btn">Save</button>
                            <button onClick={cancelEditing} className="cancel-btn">Cancel</button>
                          </td>
                        </>
                      ) : (
                        // Display row
                        <>
                          <td>{sale.date}</td>
                          <td>{sale.customer}</td>
                          <td>{sale.IGD_SKU_number}</td>
                          <td>{sale.quantity}</td>
                          <td>{formatCurrency(sale.price_per_unit)}</td>
                          <td>{formatCurrency(parseFloat(sale.quantity) * parseFloat(sale.price_per_unit))}</td>
                          <td>
                            <button onClick={() => startEditing(sale)} className="edit-btn">Edit</button>
                            <button onClick={() => handleDeleteSale(sale.id)} className="delete-btn">Delete</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradeSalesManagement; 